import axios from 'axios';
import { toast } from 'sonner';

// Domaine canonique de production : https://kadmi-ai.com
// Toutes les requêtes API en production utilisent ce domaine unique
export const getApiUrl = () => {
  // En production/preview : utiliser l'URL d'environnement si disponible
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  if (backendUrl) {
    return backendUrl.endsWith('/api') ? backendUrl : `${backendUrl}/api`;
  }
  
  // Fallback : utiliser l'origine actuelle (fonctionne sur tous les deployments)
  return `${window.location.origin}/api`;
};

const API_URL = getApiUrl();

// Log l'URL API au démarrage pour debug
if (typeof window !== 'undefined') {
  console.log('[KadmiAi] API URL configurée:', API_URL);
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important pour CORS avec credentials
});

// Flag pour éviter les boucles infinies de refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Intercepteur pour ajouter le token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs et le refresh automatique
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Si 401 et pas déjà en train de retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Si c'est une requête de login/register/refresh, ne pas retry
      if (originalRequest.url?.includes('/auth/login') || 
          originalRequest.url?.includes('/auth/register') ||
          originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }
      
      if (isRefreshing) {
        // Mettre en queue les requêtes pendant le refresh
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => Promise.reject(err));
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (refreshToken) {
        try {
          console.log('[KadmiAi API] Attempting token refresh...');
          const response = await axios.post(`${API_URL}/auth/refresh`, { 
            refresh_token: refreshToken 
          });
          
          const { access_token, refresh_token: newRefreshToken } = response.data;
          
          localStorage.setItem('token', access_token);
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }
          
          api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          
          processQueue(null, access_token);
          console.log('[KadmiAi API] Token refreshed, retrying request...');
          
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          console.error('[KadmiAi API] Refresh failed, logging out');
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          // Rediriger seulement si pas déjà sur login
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // Pas de refresh token, déconnecter
        console.log('[KadmiAi API] No refresh token, logging out');
        localStorage.removeItem('token');
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }
    
    // Si 402 (Payment Required) → limite de scan atteinte → toast + redirection /pricing
    if (error.response?.status === 402) {
      const detail = error.response?.data?.detail;
      const isScanLimit = typeof detail === 'object' && detail?.code === 'scan_limit_reached';
      if (isScanLimit) {
        const alreadyOnPricing = window.location.pathname.includes('/pricing');
        // Throttle : éviter de spammer si plusieurs requêtes en parallèle
        if (!window.__kadmi_scanlimit_shown) {
          window.__kadmi_scanlimit_shown = true;
          setTimeout(() => { window.__kadmi_scanlimit_shown = false; }, 4000);
          toast.error(detail.message || 'Limite de scans atteinte ce mois', {
            description: `Vous avez utilisé vos ${detail.scan_limit ?? '?'} scans. Passez à un plan supérieur pour continuer.`,
            action: !alreadyOnPricing ? {
              label: 'Voir les plans',
              onClick: () => { window.location.href = detail.upgrade_url || '/pricing'; },
            } : undefined,
            duration: 6000,
          });
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;
